import { useId, useMemo } from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';

export type UnstructuredEvacuationsProps = {
  regions: GeoEventRegion[];
  selectedRegionIds: number[];
  onChange: (regionIds: number[]) => void;
  disabled?: boolean;
};

/**
 * Lets reporters flag which of the incident's counties have evacuations that
 * exist but aren't mapped as structured evacuation zones.
 */
export const UnstructuredEvacuations = (
  props: UnstructuredEvacuationsProps,
): JSX.Element | null => {
  const { regions, selectedRegionIds, onChange, disabled = false } = props;
  const { t } = useTranslation();
  const titleId = useId();

  const sortedRegions = useMemo(
    () =>
      [...regions].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [regions],
  );

  const handleToggle = (regionId: number) => (): void => {
    onChange(
      selectedRegionIds.includes(regionId)
        ? selectedRegionIds.filter((id) => id !== regionId)
        : [...selectedRegionIds, regionId],
    );
  };

  if (!sortedRegions.length) {
    return null;
  }

  return (
    <FormControl
      component="fieldset"
      aria-labelledby={titleId}
      disabled={disabled}
      fullWidth
    >
      <Stack spacing={0.5}>
        <Typography id={titleId} component="h3" fontWeight="bold">
          {t('geoEventEvacuations.unstructuredEvacuations.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('geoEventEvacuations.unstructuredEvacuations.helperText')}
        </Typography>
      </Stack>

      <FormGroup sx={{ pt: 1 }}>
        {sortedRegions.map((region) => (
          <FormControlLabel
            key={region.id}
            control={
              <Checkbox
                checked={selectedRegionIds.includes(region.id)}
                onChange={handleToggle(region.id)}
              />
            }
            label={`${region.displayName}, ${region.state}`}
            sx={{ width: 'fit-content' }}
          />
        ))}
      </FormGroup>
    </FormControl>
  );
};
